// Locales: BCP 47 tags as content and the UI use them (`de`, `pt-BR`, `zh-Hans`); `en` is the source.

export const SOURCE_LOCALE = "en";

export type Direction = "ltr" | "rtl";

const RTL_LANGUAGES = new Set(["ar", "arc", "ckb", "dv", "fa", "he", "ks", "ps", "sd", "ug", "ur", "yi"]);
const RTL_SCRIPTS = new Set(["Arab", "Hebr", "Thaa", "Syrc", "Nkoo", "Adlm", "Rohg"]);

/** Direction of a tag by its script subtag, else its language. */
export function directionOf(tag: string): Direction {
  const [language = "", ...rest] = tag.split("-");
  const script = rest.find((p) => p.length === 4);
  if (script) return RTL_SCRIPTS.has(script) ? "rtl" : "ltr";
  return RTL_LANGUAGES.has(language) ? "rtl" : "ltr";
}

/** Tag of a content locale entity key (`text/<key>`): `pt_br` → `pt-BR`, `zh_hans` → `zh-Hans`. */
export function tagOfKey(key: string): string {
  return key
    .split("_")
    .map((p, i) => (i === 0 ? p : p.length === 4 ? p[0]!.toUpperCase() + p.slice(1) : p.toUpperCase()))
    .join("-");
}

/** The tag in `available` nearest to `wanted`: exact, then same language, else null. */
export function nearest(wanted: string, available: readonly string[]): string | null {
  if (available.includes(wanted)) return wanted;
  const language = wanted.split("-")[0];
  return available.find((t) => t === language) ?? available.find((t) => t.split("-")[0] === language) ?? null;
}

/** The first browser language we have, else the source locale. */
export function browserLocale(available: readonly string[]): string {
  for (const l of navigator.languages ?? [navigator.language]) {
    const hit = nearest(l, available);
    if (hit) return hit;
  }
  return SOURCE_LOCALE;
}

/** A locale's own name ("Deutsch", "日本語", "العربية"), for language menus. */
export function nativeName(tag: string): string {
  try {
    const name = new Intl.DisplayNames([tag], { type: "language" }).of(tag);
    return name ? name.charAt(0).toLocaleUpperCase(tag) + name.slice(1) : tag;
  } catch {
    return tag;
  }
}
